'use client';

import { useEffect, useRef } from 'react';
import type { LiveSite } from '@content-removal/shared';

/** Each ring is a kind of place, so where a dot lands says where it was found. */
const RINGS = [0.46, 0.66, 0.86];
const SIZE = 168;

function ringOf(site: LiveSite): number {
  if (site.impersonation) return 1;
  return site.inGoogle > 0 ? 0 : 2;
}

function css(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** A colour token with an alpha, whatever notation the token is written in. */
function faded(colour: string, alpha: number): string {
  return `color-mix(in srgb, ${colour} ${Math.round(alpha * 100)}%, transparent)`;
}

/** Brand colours at full strength shout next to the stat cards; greyed toward the muted ink they sit back. */
function calm(colour: string): string {
  return `color-mix(in srgb, ${colour} 60%, ${css('--muted')})`;
}

/**
 * The search you can watch: a sweep while it runs, a dot per site as found, the
 * count in the middle; when done a fainter sweep keeps pinging dots so the page
 * never looks idle. Purple searching, green breathing under protection, the
 * brand's violet-to-pink when nothing protects her; a pink dot hurts most.
 */
export function Radar({
  sites,
  running,
  protectedNow,
  done,
  onPick,
}: {
  sites: LiveSite[];
  running: boolean;
  protectedNow: boolean;
  /** 0–1: how far the search has got, drawn round the count. */
  done: number;
  onPick: (index: number) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const state = useRef({ sites, running, protectedNow, done });
  state.current = { sites, running, protectedNow, done };
  const born = useRef(new Map<string, number>());

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    el.width = el.height = SIZE * dpr;
    ctx.scale(dpr, dpr);
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let sweep = -Math.PI / 2;
    let last = 0;
    let frame = 0;

    const draw = (t: number) => {
      const { sites: now, running: on, protectedNow: safe, done: progress } = state.current;
      const dt = last ? (t - last) / 1000 : 0;
      last = t;
      if (!still) sweep = (sweep + dt * (on ? 1.5 : 0.7)) % (Math.PI * 2);
      const c = SIZE / 2;
      const r = c - 3;
      const primary = calm(css('--primary'));
      const from = calm(css('--brand-grad-from'));
      const to = calm(css('--brand-grad-to'));
      // calm() greys the resolved green to olive; it is the one colour meant to read as good news.
      const ok = `color-mix(in srgb, ${css('--resolved')} 50%, #22c55e)`;
      const brand = ctx.createLinearGradient(0, 0, SIZE, SIZE);
      brand.addColorStop(0, from);
      brand.addColorStop(1, to);
      const tone = on ? primary : safe ? ok : brand;
      ctx.clearRect(0, 0, SIZE, SIZE);

      RINGS.forEach((k, i) => {
        ctx.strokeStyle = i === 2 ? brand : faded(primary, 0.28);
        ctx.globalAlpha = i === 2 ? 0.45 : 1;
        ctx.lineWidth = i === 2 ? 1.5 : 1;
        ctx.setLineDash(i === 2 ? [] : [2, 4]);
        ctx.beginPath();
        ctx.arc(c, c, r * k, 0, Math.PI * 2);
        ctx.stroke();
      });
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);
      ctx.lineCap = 'round';
      for (let i = 0; i < 48; i++) {
        const a = (i / 48) * Math.PI * 2;
        const inner = r - (i % 4 === 0 ? 6 : 3);
        ctx.strokeStyle = faded(primary, i % 4 === 0 ? 0.45 : 0.22);
        ctx.beginPath();
        ctx.moveTo(c + Math.cos(a) * inner, c + Math.sin(a) * inner);
        ctx.lineTo(c + Math.cos(a) * r, c + Math.sin(a) * r);
        ctx.stroke();
      }
      ctx.lineCap = 'butt';

      if (on || !still) {
        const beam = on ? primary : safe ? ok : from;
        if ('createConicGradient' in ctx) {
          const g = ctx.createConicGradient(sweep - 1.2, c, c);
          g.addColorStop(0, faded(beam, 0));
          g.addColorStop(0.19, faded(beam, on ? 0.28 : 0.16));
          g.addColorStop(0.191, faded(beam, 0));
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(c, c, r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.strokeStyle = on ? beam : faded(beam, 0.55);
        ctx.lineWidth = on ? 2 : 1.5;
        ctx.beginPath();
        ctx.moveTo(c, c);
        ctx.lineTo(c + Math.cos(sweep) * r, c + Math.sin(sweep) * r);
        ctx.stroke();
      }
      if (!on && safe && !still) {
        const ph = (t / 3200) % 1;
        ctx.strokeStyle = faded(ok, 0.3 * (1 - ph));
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(c, c, 30 + ph * (r - 30), 0, Math.PI * 2);
        ctx.stroke();
      }

      now.forEach((site, i) => {
        const key = site.domain;
        if (!born.current.has(key)) born.current.set(key, t);
        const age = (t - (born.current.get(key) ?? t)) / 1000;
        const ring = ringOf(site);
        const a = (i * 2.39996) % (Math.PI * 2);
        const k = RINGS[ring] + (((i * 37) % 7) - 3) / 100;
        const x = c + Math.cos(a) * r * k;
        const y = c + Math.sin(a) * r * k;
        const colour = site.hot ? to : from;
        const size = 3.2 + Math.min(4, site.linkCount) * 0.9;
        if (!still && age < 1.4) {
          ctx.strokeStyle = faded(colour, 1 - age / 1.4);
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(x, y, size + age * 14, 0, Math.PI * 2);
          ctx.stroke();
        } else if (!still && !on) {
          // How far the beam has gone past this dot; it pings for the first half radian.
          const behind = (((sweep - a) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
          if (behind < 0.5) {
            const p = behind / 0.5;
            ctx.strokeStyle = faded(colour, 0.6 * (1 - p));
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(x, y, size + 2 + p * 9, 0, Math.PI * 2);
            ctx.stroke();
          }
        }
        ctx.fillStyle = faded(colour, 0.18);
        ctx.beginPath();
        ctx.arc(x, y, size + 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = colour;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = css('--surface');
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });

      const d = 29;
      ctx.shadowColor = faded(to, 0.25);
      ctx.shadowBlur = 12;
      ctx.fillStyle = css('--surface');
      ctx.beginPath();
      ctx.arc(c, c, d, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = faded(primary, 0.14);
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.arc(c, c, d, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = tone;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(c, c, d, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (on ? progress : 1));
      ctx.stroke();
      ctx.lineCap = 'butt';
      ctx.fillStyle = css('--ink');
      ctx.font = `700 24px ${css('--font-display') || 'Inter, sans-serif'}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(now.length), c, c - 4);
      ctx.fillStyle = on ? primary : safe ? ok : to;
      ctx.font = '700 8.5px Inter, sans-serif';
      ctx.fillText(on ? `${Math.round(progress * 100)}%` : now.length === 1 ? 'SITE' : 'SITES', c, c + 13);
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  const pick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - box.left) / box.width) * SIZE;
    const y = ((event.clientY - box.top) / box.height) * SIZE;
    const c = SIZE / 2;
    const r = c - 3;
    const hit = sites.findIndex((site, i) => {
      const a = (i * 2.39996) % (Math.PI * 2);
      const k = RINGS[ringOf(site)] + (((i * 37) % 7) - 3) / 100;
      return Math.hypot(c + Math.cos(a) * r * k - x, c + Math.sin(a) * r * k - y) < 9;
    });
    if (hit >= 0) onPick(hit);
  };

  return (
    <canvas
      ref={canvas}
      className="ov-radar"
      onClick={pick}
      aria-label="Live search: each dot is a site, the inner ring search engines, the middle social platforms, the outer leak and tube sites"
    />
  );
}
